import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';
import {
  Download, FileText, FileJson, RefreshCw, Database,
  CheckCircle, History, Cloud, CloudOff, Link2, Unlink,
  ExternalLink, HardDrive, Upload, Settings, FolderOpen
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function DataExportPage() {
  const [modules, setModules] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(null);
  const [syncing, setSyncing] = useState(null);
  const [format, setFormat] = useState('csv');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [tab, setTab] = useState('export');

  // Google Drive state
  const [driveStatus, setDriveStatus] = useState({ connected: false, configured: false });
  const [driveFiles, setDriveFiles] = useState([]);
  const [driveHistory, setDriveHistory] = useState([]);
  const [autoSync, setAutoSync] = useState({ enabled: false, modules: [], frequency: 'daily', format: 'csv' });
  const [showAutoSync, setShowAutoSync] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [m, h, ds] = await Promise.all([
        axios.get(`${API}/api/data-export/modules`),
        axios.get(`${API}/api/data-export/history`),
        axios.get(`${API}/api/drive/status`).catch(() => ({ data: { connected: false, configured: false } })),
      ]);
      setModules(m.data.modules || []);
      setHistory(h.data.history || []);
      setDriveStatus(ds.data);

      if (ds.data.connected) {
        const [df, dh, as_] = await Promise.all([
          axios.get(`${API}/api/drive/files`).catch(() => ({ data: { files: [] } })),
          axios.get(`${API}/api/drive/sync-history`).catch(() => ({ data: { history: [] } })),
          axios.get(`${API}/api/drive/auto-sync`).catch(() => ({ data: { enabled: false, modules: [], frequency: 'daily', format: 'csv' } })),
        ]);
        setDriveFiles(df.data.files || []);
        setDriveHistory(dh.data.history || []);
        setAutoSync(as_.data);
      }
    } catch { /* silent */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Check URL params for drive=connected redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('drive') === 'connected') {
      toast.success('Google Drive connected successfully!');
      window.history.replaceState({}, '', window.location.pathname);
      fetchData();
    }
  }, [fetchData]);

  const handleExport = async (mod) => {
    setExporting(mod.key);
    try {
      let url = `${API}/api/data-export/download/${mod.key}?format=${format}`;
      if (dateFrom) url += `&date_from=${dateFrom}`;
      if (dateTo) url += `&date_to=${dateTo}`;
      const res = await axios.get(url, { responseType: 'blob' });
      const ext = format === 'json' ? 'json' : 'csv';
      const blob = new Blob([res.data], { type: format === 'json' ? 'application/json' : 'text/csv' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${mod.key}_export.${ext}`;
      link.click();
      URL.revokeObjectURL(link.href);
      await axios.post(`${API}/api/data-export/log`, { module: mod.key, format, record_count: mod.record_count });
      toast.success(`Exported ${mod.label} as ${format.toUpperCase()}`);
      fetchData();
    } catch {
      toast.error('Export failed');
    } finally { setExporting(null); }
  };

  const handleDriveSync = async (mod) => {
    setSyncing(mod.key);
    try {
      const res = await axios.post(`${API}/api/drive/sync/${mod.key}?format=${format}`, {});
      toast.success(<span>Synced to Drive: <a href={res.data.drive_link} target="_blank" rel="noopener noreferrer" className="underline">{res.data.filename}</a></span>);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Drive sync failed');
    } finally { setSyncing(null); }
  };

  const connectDrive = async () => {
    try {
      const res = await axios.get(`${API}/api/drive/connect`);
      window.location.href = res.data.authorization_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to connect Google Drive');
    }
  };

  const disconnectDrive = async () => {
    if (!window.confirm('Disconnect Google Drive? Existing synced files will remain in Drive.')) return;
    try {
      await axios.delete(`${API}/api/drive/disconnect`);
      toast.success('Google Drive disconnected');
      setDriveStatus({ connected: false, configured: driveStatus.configured });
      setDriveFiles([]);
      setDriveHistory([]);
    } catch { toast.error('Failed to disconnect'); }
  };

  const saveAutoSync = async () => {
    try {
      await axios.put(`${API}/api/drive/auto-sync`, autoSync);
      toast.success('Auto-sync settings saved');
      setShowAutoSync(false);
    } catch { toast.error('Failed to save'); }
  };

  const toggleAutoSyncModule = (key) => {
    setAutoSync(prev => ({
      ...prev,
      modules: prev.modules.includes(key) ? prev.modules.filter(m => m !== key) : [...prev.modules, key],
    }));
  };

  if (loading) return <div className="flex justify-center p-12"><RefreshCw className="h-5 w-5 animate-spin text-gray-400" /></div>;

  const formatBytes = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="data-export-page">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-text-primary">
            Data Export<PageInfoTooltip pageKey="data-export" />
          </h1>
          <p className="text-gray-medium text-sm mt-1">Download your business data or sync to Google Drive</p>
        </div>
        {/* Drive connection badge */}
        <DriveStatusBadge status={driveStatus} onConnect={connectDrive} onDisconnect={disconnectDrive} />
      </div>

      {/* Tabs */}
      <div className="flex gap-2 flex-wrap">
        <Button variant={tab === 'export' ? 'default' : 'outline'} size="sm" onClick={() => setTab('export')} data-testid="tab-export">
          <Download className="h-3.5 w-3.5 mr-1.5" />Export
        </Button>
        <Button variant={tab === 'history' ? 'default' : 'outline'} size="sm" onClick={() => setTab('history')} data-testid="tab-history">
          <History className="h-3.5 w-3.5 mr-1.5" />History
        </Button>
        {driveStatus.connected && (
          <>
            <Button variant={tab === 'drive' ? 'default' : 'outline'} size="sm" onClick={() => setTab('drive')} data-testid="tab-drive">
              <Cloud className="h-3.5 w-3.5 mr-1.5" />Drive Files
            </Button>
            <Button variant={tab === 'sync-history' ? 'default' : 'outline'} size="sm" onClick={() => setTab('sync-history')} data-testid="tab-sync-history">
              <Upload className="h-3.5 w-3.5 mr-1.5" />Sync History
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowAutoSync(true)} data-testid="auto-sync-btn">
              <Settings className="h-3.5 w-3.5 mr-1.5" />Auto-Sync
            </Button>
          </>
        )}
      </div>

      {/* Export Tab */}
      {tab === 'export' && (
        <>
          <Card className="bg-white border-gray-border">
            <CardContent className="pt-4">
              <div className="flex flex-wrap items-end gap-3 mb-4">
                <div>
                  <Label className="text-xs">Format</Label>
                  <Select value={format} onValueChange={setFormat}>
                    <SelectTrigger className="w-28 h-8" data-testid="format-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="csv"><span className="flex items-center gap-1.5"><FileText className="h-3 w-3" />CSV</span></SelectItem>
                      <SelectItem value="json"><span className="flex items-center gap-1.5"><FileJson className="h-3 w-3" />JSON</span></SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">From Date</Label>
                  <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-8 w-36" data-testid="date-from" />
                </div>
                <div>
                  <Label className="text-xs">To Date</Label>
                  <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-8 w-36" data-testid="date-to" />
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {modules.map(mod => (
              <Card key={mod.key} className="bg-white border-gray-border hover:shadow-md transition-shadow" data-testid={`module-${mod.key}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
                        <Database className="h-4 w-4 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium text-sm text-text-primary">{mod.label}</p>
                        <p className="text-[10px] text-gray-medium">{mod.record_count} records</p>
                      </div>
                    </div>
                    <Badge className={cn("text-[10px]", mod.record_count > 0 ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500")}>
                      {mod.record_count > 0 ? 'Has Data' : 'Empty'}
                    </Badge>
                  </div>
                  <div className="text-[10px] text-gray-400 mb-3 truncate">{mod.fields.join(', ')}</div>
                  <div className="flex gap-2">
                    <Button
                      size="sm" className="flex-1 h-8 text-xs"
                      disabled={mod.record_count === 0 || exporting === mod.key}
                      onClick={() => handleExport(mod)}
                      data-testid={`export-${mod.key}`}
                    >
                      {exporting === mod.key ? <RefreshCw className="h-3 w-3 mr-1 animate-spin" /> : <Download className="h-3 w-3 mr-1" />}
                      {format.toUpperCase()}
                    </Button>
                    {driveStatus.connected && (
                      <Button
                        size="sm" variant="outline" className="h-8 text-xs"
                        disabled={mod.record_count === 0 || syncing === mod.key}
                        onClick={() => handleDriveSync(mod)}
                        data-testid={`drive-sync-${mod.key}`}
                      >
                        {syncing === mod.key ? <RefreshCw className="h-3 w-3 mr-1 animate-spin" /> : <Cloud className="h-3 w-3 mr-1" />}
                        Drive
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}

      {/* Export History Tab */}
      {tab === 'history' && (
        <Card className="bg-white border-gray-border">
          <CardContent className="pt-4">
            {history.length === 0 ? (
              <EmptyState icon={History} text="No exports yet" />
            ) : (
              <div className="space-y-2">
                {history.map((h, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 rounded-lg border border-gray-border" data-testid={`history-row-${i}`}>
                    <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="h-4 w-4 text-emerald-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm text-text-primary capitalize">{h.module?.replace(/_/g, ' ')}</span>
                        <Badge className="bg-gray-100 text-gray-600 text-[10px]">{h.format?.toUpperCase()}</Badge>
                        <Badge className="bg-blue-100 text-blue-600 text-[10px]">{h.record_count} records</Badge>
                      </div>
                      <p className="text-xs text-gray-medium">by {h.exported_by} &middot; {h.exported_at ? new Date(h.exported_at).toLocaleDateString() : ''}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Drive Files Tab */}
      {tab === 'drive' && driveStatus.connected && (
        <Card className="bg-white border-gray-border">
          <CardContent className="pt-4">
            {driveFiles.length === 0 ? (
              <EmptyState icon={FolderOpen} text="No files in Konnect Exports folder yet" />
            ) : (
              <div className="space-y-2">
                {driveFiles.map((f, i) => (
                  <div key={f.id} className="flex items-center gap-3 p-3 rounded-lg border border-gray-border hover:bg-gray-50 transition-colors" data-testid={`drive-file-${i}`}>
                    <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                      <HardDrive className="h-4 w-4 text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-text-primary truncate">{f.name}</p>
                      <p className="text-xs text-gray-medium">{formatBytes(f.size)} &middot; {f.modifiedTime ? new Date(f.modifiedTime).toLocaleDateString() : ''}</p>
                    </div>
                    {f.webViewLink && (
                      <a href={f.webViewLink} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700" data-testid={`drive-link-${i}`}>
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Drive Sync History Tab */}
      {tab === 'sync-history' && driveStatus.connected && (
        <Card className="bg-white border-gray-border">
          <CardContent className="pt-4">
            {driveHistory.length === 0 ? (
              <EmptyState icon={Upload} text="No Drive syncs yet" />
            ) : (
              <div className="space-y-2">
                {driveHistory.map((h, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 rounded-lg border border-gray-border" data-testid={`sync-row-${i}`}>
                    <div className="w-8 h-8 rounded-lg bg-teal-50 flex items-center justify-center flex-shrink-0">
                      <Cloud className="h-4 w-4 text-teal-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm text-text-primary">{h.filename}</span>
                        <Badge className="bg-gray-100 text-gray-600 text-[10px]">{h.format?.toUpperCase()}</Badge>
                        <Badge className="bg-blue-100 text-blue-600 text-[10px]">{h.record_count} records</Badge>
                        {h.file_size > 0 && <Badge className="bg-gray-100 text-gray-500 text-[10px]">{formatBytes(h.file_size)}</Badge>}
                      </div>
                      <p className="text-xs text-gray-medium">by {h.synced_by} &middot; {h.synced_at ? new Date(h.synced_at).toLocaleDateString() : ''}</p>
                    </div>
                    {h.drive_link && (
                      <a href={h.drive_link} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700">
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Auto-Sync Settings Dialog */}
      <Dialog open={showAutoSync} onOpenChange={setShowAutoSync}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Settings className="h-5 w-5 text-blue-600" />Auto-Sync Settings</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>Enable Auto-Sync</Label>
              <Switch checked={autoSync.enabled} onCheckedChange={v => setAutoSync(p => ({ ...p, enabled: v }))} data-testid="auto-sync-toggle" />
            </div>
            <div>
              <Label className="text-xs">Frequency</Label>
              <Select value={autoSync.frequency} onValueChange={v => setAutoSync(p => ({ ...p, frequency: v }))}>
                <SelectTrigger className="mt-1" data-testid="sync-frequency"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="hourly">Every Hour</SelectItem>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Export Format</Label>
              <Select value={autoSync.format} onValueChange={v => setAutoSync(p => ({ ...p, format: v }))}>
                <SelectTrigger className="mt-1" data-testid="sync-format"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="csv">CSV</SelectItem>
                  <SelectItem value="json">JSON</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs mb-2 block">Modules to Sync</Label>
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {modules.map(m => (
                  <label key={m.key} className="flex items-center gap-2 p-2 rounded-lg border border-gray-border hover:bg-gray-50 cursor-pointer">
                    <input type="checkbox" checked={autoSync.modules.includes(m.key)} onChange={() => toggleAutoSyncModule(m.key)}
                      className="rounded border-gray-300" data-testid={`sync-module-${m.key}`} />
                    <span className="text-xs flex-1">{m.label}</span>
                    <span className="text-[10px] text-gray-400">{m.record_count} records</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAutoSync(false)}>Cancel</Button>
            <Button onClick={saveAutoSync} data-testid="save-auto-sync"><CheckCircle className="h-4 w-4 mr-1" />Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EmptyState({ icon: Icon, text }) {
  return (
    <div className="text-center py-12 text-gray-medium">
      <Icon className="h-10 w-10 mx-auto mb-3 opacity-30" />
      <p className="text-sm">{text}</p>
    </div>
  );
}

function DriveStatusBadge({ status, onConnect, onDisconnect }) {
  if (status.connected) {
    return (
      <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2" data-testid="drive-connected-badge">
        <Cloud className="h-4 w-4 text-emerald-600" />
        <div>
          <p className="text-xs font-medium text-emerald-700">Google Drive Connected</p>
          {status.user_email && <p className="text-[10px] text-emerald-600">{status.user_email}</p>}
        </div>
        <Button size="sm" variant="ghost" className="h-7 text-xs text-red-500 hover:text-red-600 ml-2" onClick={onDisconnect} data-testid="disconnect-drive-btn">
          <Unlink className="h-3 w-3" />
        </Button>
      </div>
    );
  }

  if (status.configured) {
    return (
      <Button variant="outline" size="sm" onClick={onConnect} data-testid="connect-drive-btn">
        <Cloud className="h-4 w-4 mr-1.5" />Connect Google Drive
      </Button>
    );
  }

  return (
    <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2" data-testid="drive-not-configured">
      <CloudOff className="h-4 w-4 text-gray-400" />
      <p className="text-[10px] text-gray-500">Google Drive not configured. Add credentials in Settings &rarr; Enterprise Integrations.</p>
    </div>
  );
}
